import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import DataTable from "react-data-table-component";
import {
  Box,
  Stack,
  Paper,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  TextField,
  InputAdornment,
  Avatar,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Skeleton,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Replay as ReplayIcon,
  Close as CloseIcon,
  Visibility,
  ContactEmergency,
  CheckCircleOutline,
  ErrorOutline,
  HourglassEmpty,
  PlayCircleOutline,
  CancelOutlined,
  TrendingUp,
  WorkOutline as JobIcon,
  SettingsOutlined,
  Business as BusinessIcon,
} from "@mui/icons-material";
import CircularProgress from "@mui/material/CircularProgress";
import { useNavigate } from "react-router-dom";
import { t } from "i18next";
import moment from "moment";
import useAPI from "hooks/useAPI";
import useAlert from "hooks/useAlert";
import useConstants from "constants";
import { usePermissions } from "context/PermissionsContext";
import AlertMessage from "components/UI/AlertMessage";
import SoftButton from "components/SoftButton";
import MBDrawer from "components/MBDrawer";
import PageSettings from "components/PageSettings";
import SortIcon from "components/SortIcon";
import { formatDateWithFormat } from "utils/utilities";
import WithPermission from "components/WithPermission";

const SEARCH_DEBOUNCE_MS = 400;

// ─── Status meta ──────────────────────────────────────────────────────────────

const STATUS_META = {
  completed: { color: "#22c55e", bg: "rgba(34,197,94,0.10)", label: "Completed" },
  failed: { color: "#ef4444", bg: "rgba(239,68,68,0.10)", label: "Failed" },
  running: { color: "#3b82f6", bg: "rgba(59,130,246,0.10)", label: "Running" },
  pending: { color: "#f59e0b", bg: "rgba(245,158,11,0.10)", label: "Pending" },
  cancelled: { color: "#6b7280", bg: "rgba(107,114,128,0.10)", label: "Cancelled" },
  processing: { color: "#8b5cf6", bg: "rgba(139,92,246,0.10)", label: "Processing" },
};

const StatusChip = ({ status }) => {
  const meta = STATUS_META[status] || { color: "#9ca3af", bg: "rgba(0,0,0,0.06)", label: status };
  return (
    <Chip
      label={meta.label}
      size="small"
      sx={{
        background: meta.bg,
        color: meta.color,
        border: `1px solid ${meta.color}44`,
        fontWeight: 700,
        fontSize: "0.7rem",
        height: 22,
      }}
    />
  );
};

StatusChip.propTypes = { status: PropTypes.string.isRequired };

const toTitleCase = (str) => {
  if (!str) return "";
  return str
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

// ─── Mini summary card ────────────────────────────────────────────────────────

const MiniStat = ({ label, value, color, icon }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: 2.5,
      border: "1.5px solid",
      borderColor: `${color}33`,
      background: `${color}0a`,
      flex: 1,
    }}
  >
    <CardContent sx={{ p: "14px 16px !important" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
            {label}
          </Typography>
          <Typography variant="h5" fontWeight={800} sx={{ color, lineHeight: 1, mt: 0.25 }}>
            {value}
          </Typography>
        </Box>
        <Avatar sx={{ bgcolor: `${color}18`, color, width: 36, height: 36 }}>{icon}</Avatar>
      </Stack>
    </CardContent>
  </Card>
);

MiniStat.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  color: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
};

// ─── Expanded row ─────────────────────────────────────────────────────────────

const ExpandedRow = ({ data, dateFormat, dateTimeFormat }) => {
  const formatPayload = (payload) => {
    if (!payload) return "No payload available";
    try {
      const parsed = typeof payload === "string" ? JSON.parse(payload) : payload;
      const copy = JSON.parse(JSON.stringify(parsed));
      if (
        (data.name === "pull_controller_data" || data.name === "pull_valves_data") &&
        copy?.start_date
      ) {
        const val = copy.start_date;
        if (typeof val === "string" && /[^0-9]/.test(val)) {
          copy.start_date = val;
        } else {
          const ms = typeof val === "string" ? parseInt(val, 10) : val;
          if (!isNaN(ms) && ms > 0) copy.start_date = moment(ms).format(dateFormat);
        }
      }
      return JSON.stringify(copy, null, 2);
    } catch {
      return typeof payload === "string" ? payload : JSON.stringify(payload, null, 2);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 2,
          background: "rgba(0,0,0,0.02)",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography
          variant="caption"
          fontWeight={700}
          color="text.secondary"
          sx={{ mb: 1, display: "block", textTransform: "uppercase", letterSpacing: 0.5 }}
        >
          Payload Details
        </Typography>
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1.5,
            backgroundColor: "background.paper",
            borderRadius: 1.5,
            overflow: "auto",
            fontSize: "0.8rem",
            fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            maxHeight: "300px",
            border: "1px solid",
            borderColor: "divider",
            color: "text.primary",
          }}
        >
          {formatPayload(data.raw_payload)}
        </Box>
      </Paper>
    </Box>
  );
};

ExpandedRow.propTypes = {
  data: PropTypes.object.isRequired,
  dateFormat: PropTypes.string.isRequired,
  dateTimeFormat: PropTypes.string.isRequired,
};

// ─── Main Component ───────────────────────────────────────────────────────────

const JobRecords = ({ jobName }) => {
  const { api } = useAPI();
  const { errorState, showAlert } = useAlert();
  const { per_page, items_per_page_options, date_format, date_time_format } = useConstants();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();

  const table_id = "jobs";
  const default_sort_column = "id";
  const default_sort_direction = "desc";

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoaded, setSearchLoaded] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(per_page);
  const [searchInput, setSearchInput] = useState("");
  const [businessIdInput, setBusinessIdInput] = useState("");
  const [orderColumn, setOrderColumn] = useState(default_sort_column);
  const [orderDirection, setOrderDirection] = useState(default_sort_direction);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Settings drawer
  const [openSettingsDrawer, setOpenSettingsDrawer] = useState(false);
  const [tableId, setTableId] = useState(null);
  const [settingsSaveBtnLoading, setSettingsSaveBtnLoading] = useState(false);
  const [defaultSortColumnFromSettings, setDefaultSortColumnFromSettings] =
    useState(default_sort_column);
  const [defaultSortDirectionFromSettings, setDefaultSortDirectionFromSettings] =
    useState(default_sort_direction);
  const [defaultItemsPerPageFromSettings, setDefaultItemsPerPageFromSettings] = useState(per_page);

  // Actions
  const [retryingJobId, setRetryingJobId] = useState(null);
  const [cancellingJobId, setCancellingJobId] = useState(null);
  const [cancelPopupOpen, setCancelPopupOpen] = useState(false);
  const [cancelJobId, setCancelJobId] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [retryByPopupOpen, setRetryByPopupOpen] = useState(false);
  const [retryByData, setRetryByData] = useState([]);

  const [totalFailedJobs, setTotalFailedJobs] = useState(0);
  const [totalRunningJobs, setTotalRunningJobs] = useState(0);
  const [totalCompletedJobs, setTotalCompletedJobs] = useState(0);

  const fetchJobs = useCallback(
    async (sortCol, sortDir, pageSize, isReset, searchValue, nextPage, businessIdOverride) => {
      setLoading(true);
      let requestConfig = {};
      const resolvedSortCol = sortCol || orderColumn;
      const resolvedSortDir = sortDir || orderDirection;
      const resolvedPerPage = pageSize || perPage;
      const resolvedPage = nextPage || page;
      const bid = businessIdOverride !== undefined ? businessIdOverride : businessIdInput;
      const businessParams = bid.trim() ? { business_id: bid.trim() } : {};

      if (!isInitialLoad && !isReset) {
        requestConfig = {
          params: {
            page: resolvedPage,
            per_page: resolvedPerPage,
            search: searchValue,
            order_col: resolvedSortCol,
            order_dir: resolvedSortDir,
            name: jobName,
            ...businessParams,
          },
        };
      } else {
        requestConfig = { params: { name: jobName, ...businessParams } };
      }

      try {
        const response = await api.get("/jobs/", requestConfig);
        const result = response.data;
        const ps = result.page_settings;
        setData(result.data || []);
        setTotalRows(result.recordsTotal || 0);
        setTotalFailedJobs(result.total_failed_jobs || 0);
        setTotalRunningJobs(result.total_running_jobs || 0);
        setTotalCompletedJobs(result.total_completed_jobs || 0);
        if (isInitialLoad || isReset) {
          if (ps?.sortColumn) {
            setOrderColumn(ps.sortColumn);
            setDefaultSortColumnFromSettings(ps.sortColumn);
          }
          if (ps?.sortDirection) {
            setOrderDirection(ps.sortDirection);
            setDefaultSortDirectionFromSettings(ps.sortDirection);
          }
          if (ps?.itemsPerPage) {
            setPerPage(ps.itemsPerPage);
            setDefaultItemsPerPageFromSettings(ps.itemsPerPage);
          }
          if (isReset && (!ps || Object.keys(ps).length === 0)) {
            setOrderColumn(default_sort_column);
            setOrderDirection(default_sort_direction);
            setPerPage(per_page);
            setDefaultSortColumnFromSettings(default_sort_column);
            setDefaultSortDirectionFromSettings(default_sort_direction);
            setDefaultItemsPerPageFromSettings(per_page);
          }
          setIsInitialLoad(false);
        }
        setSearchLoaded(true);
      } catch (err) {
        showAlert("error", err?.response?.data?.error || t("lang.something_went_wrong"));
      } finally {
        setLoading(false);
      }
    },
    [api, jobName, orderColumn, orderDirection, perPage, page, isInitialLoad, businessIdInput]
  );

  const isFirstFilterEffect = useRef(true);
  const suppressDebounceOnce = useRef(false);

  useEffect(() => {
    fetchJobs(orderColumn, orderDirection, perPage, false, searchInput, page, businessIdInput);
  }, []);

  useEffect(() => {
    if (suppressDebounceOnce.current) {
      suppressDebounceOnce.current = false;
      return;
    }
    if (isFirstFilterEffect.current) {
      isFirstFilterEffect.current = false;
      return;
    }
    const timer = setTimeout(() => {
      setPage(1);
      fetchJobs(orderColumn, orderDirection, perPage, false, searchInput, 1, businessIdInput);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput, businessIdInput]);

  const handlePageChange = (p) => {
    setPage(p);
    fetchJobs(orderColumn, orderDirection, perPage, false, searchInput, p, businessIdInput);
  };
  const handlePerPageChange = (pp) => {
    setPerPage(pp);
    setPage(1);
    fetchJobs(orderColumn, orderDirection, pp, false, searchInput, 1, businessIdInput);
  };
  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
  };
  const handleSort = (col, dir) => {
    const nc = col.columnKey;
    const nd = nc === orderColumn ? (orderDirection === "asc" ? "desc" : "asc") : dir;
    setOrderColumn(nc);
    setOrderDirection(nd);
    fetchJobs(nc, nd, perPage, false, searchInput, page, businessIdInput);
  };
  const handleReset = () => {
    const hadInput = searchInput.trim() !== "" || businessIdInput.trim() !== "";
    if (hadInput) {
      suppressDebounceOnce.current = true;
    }
    setSearchInput("");
    setBusinessIdInput("");
    fetchJobs(orderColumn, orderDirection, perPage, true, "", 1, "");
  };

  const handleBusinessIdChange = (e) => {
    setBusinessIdInput(e.target.value);
  };

  const getSortIcon = (key) => (
    <SortIcon orderDirection={orderDirection} isActive={orderColumn === key} />
  );

  // Actions
  const handleCancelJob = (id) => {
    setCancelJobId(id);
    setCancelPopupOpen(true);
  };
  const handleCancelPopupClose = () => {
    setCancelPopupOpen(false);
    setCancelJobId(null);
    setCancelReason("");
  };
  const handleCancelJobConfirm = async () => {
    setCancellingJobId(cancelJobId);
    try {
      const payload = cancelReason.trim() ? { reason: cancelReason.trim() } : {};
      const res = await api.post(`/cancel-job/${cancelJobId}/`, payload);
      if (res.status === 200) {
        showAlert("success", res.data.message);
        fetchJobs(orderColumn, orderDirection, perPage, false, searchInput, page, businessIdInput);
        handleCancelPopupClose();
      }
    } catch (err) {
      showAlert("error", err?.response?.data?.error || t("lang.something_went_wrong"));
    } finally {
      setCancellingJobId(null);
    }
  };
  const handleRetryJob = async (id) => {
    setRetryingJobId(id);
    try {
      const res = await api.get(`/retry-job/${id}/`);
      if (res.status === 200) {
        showAlert("success", res.data.message);
        fetchJobs(orderColumn, orderDirection, perPage, false, searchInput, page, businessIdInput);
      }
    } catch (err) {
      showAlert("error", err?.response?.data?.error || t("lang.something_went_wrong"));
    } finally {
      setRetryingJobId(null);
    }
  };
  const handleRetryByClick = (arr) => {
    setRetryByData(arr);
    setRetryByPopupOpen(true);
  };
  const handleRetryByPopupClose = () => {
    setRetryByPopupOpen(false);
    setRetryByData([]);
  };

  const handleSettingsClick = (tid) => {
    setTableId(tid);
    setOpenSettingsDrawer(true);
  };
  const handleSettingsChange = async (settings) => {
    const ns = settings[tableId];
    setSettingsSaveBtnLoading(true);
    try {
      const res = await api.post("/user-page-settings/", { page_slug: "jobs", settings });
      if (res.status === 200) {
        setOrderColumn(ns.sortColumn);
        setOrderDirection(ns.sortDirection);
        setPerPage(ns.itemsPerPage);
        setDefaultSortColumnFromSettings(ns.sortColumn);
        setDefaultSortDirectionFromSettings(ns.sortDirection);
        setDefaultItemsPerPageFromSettings(ns.itemsPerPage);
        setPage(1);
        fetchJobs(
          ns.sortColumn,
          ns.sortDirection,
          ns.itemsPerPage,
          false,
          searchInput,
          1,
          businessIdInput
        );
        setOpenSettingsDrawer(false);
        setTableId(null);
        showAlert("success", res.data.message);
      } else {
        showAlert("error", res.data.error);
      }
    } catch (err) {
      showAlert("error", err?.response?.data?.error || "");
    } finally {
      setSettingsSaveBtnLoading(false);
    }
  };

  const columns = [
    {
      name: (
        <Stack
          direction="row"
          alignItems="center"
          gap={0.5}
          sx={{ textTransform: "uppercase", fontSize: "0.75rem", fontWeight: 700 }}
        >
          {t("lang.hash_id") || "#ID"}
          {getSortIcon("id")}
        </Stack>
      ),
      columnKey: "id",
      selector: (row) => (
        <Typography variant="body2" fontWeight={600} color="text.primary">
          #{row.id}
        </Typography>
      ),
      sortable: true,
      width: "80px",
    },
    {
      name: (
        <Stack
          direction="row"
          alignItems="center"
          gap={0.5}
          sx={{ textTransform: "uppercase", fontSize: "0.75rem", fontWeight: 700 }}
        >
          {t("lang.details") || "Details"}
          {getSortIcon("payload")}
        </Stack>
      ),
      columnKey: "payload",
      selector: (row) => (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            py: 0.5,
          }}
        >
          {row.payload}
        </Typography>
      ),
      sortable: true,
      grow: 1,
    },
    {
      name: (
        <Stack
          direction="row"
          alignItems="center"
          gap={0.5}
          sx={{ textTransform: "uppercase", fontSize: "0.75rem", fontWeight: 700 }}
        >
          {t("lang.date") || "Date"}
          {getSortIcon("created_at")}
        </Stack>
      ),
      columnKey: "created_at",
      selector: (row) => (
        <Box>
          <Typography variant="caption" color="text.primary" fontWeight={500}>
            {formatDateWithFormat(row.created_at, date_time_format)}
          </Typography>
        </Box>
      ),
      sortable: true,
      width: "160px",
    },
    {
      name: (
        <Stack
          direction="row"
          alignItems="center"
          gap={0.5}
          sx={{ textTransform: "uppercase", fontSize: "0.75rem", fontWeight: 700 }}
        >
          {t("lang.status") || "Status"}
          {getSortIcon("status")}
        </Stack>
      ),
      columnKey: "status",
      selector: (row) => <StatusChip status={row.status} />,
      sortable: true,
      width: "120px",
    },
  ];

  if (hasPermission("can_view_gwm")) {
    columns.push({
      name: (
        <Typography sx={{ textTransform: "uppercase", fontSize: "0.75rem", fontWeight: 700 }}>
          {t("lang.actions.action") || "Actions"}
        </Typography>
      ),
      selector: (row) => (
        <Stack direction="row" alignItems="center" gap={0.25}>
          {!row.is_default && (
            <WithPermission permission="can_view_gwm">
              <Tooltip title={t("lang.view_job") || "View Sub-Jobs"} placement="top">
                <IconButton size="small" onClick={() => navigate(`/job/${row.id}`)}>
                  <Visibility fontSize="small" color="primary" />
                </IconButton>
              </Tooltip>
            </WithPermission>
          )}
          {row.id === retryingJobId || row.id === cancellingJobId ? (
            <IconButton size="small">
              <CircularProgress size={16} color="secondary" />
            </IconButton>
          ) : (
            row.can_retry && (
              <>
                <WithPermission permission="can_view_gwm">
                  <Tooltip
                    title={
                      row.can_retry_sub_jobs ? t("lang.retry") : t("lang.cannot_retry_sub_jobs")
                    }
                    placement="top"
                  >
                    <IconButton
                      size="small"
                      onClick={() => row.can_retry_sub_jobs && handleRetryJob(row.id)}
                    >
                      <ReplayIcon
                        fontSize="small"
                        color={row.can_retry_sub_jobs ? "secondary" : "disabled"}
                      />
                    </IconButton>
                  </Tooltip>
                </WithPermission>
                <WithPermission permission="can_view_gwm">
                  <Tooltip title={t("lang.cancel") || "Cancel"} placement="top">
                    <IconButton size="small" onClick={() => handleCancelJob(row.id)}>
                      <CloseIcon fontSize="small" color="error" />
                    </IconButton>
                  </Tooltip>
                </WithPermission>
              </>
            )
          )}
          {row.retry_by?.length > 0 && (
            <Tooltip title={t("lang.retry_by") || "Retry By"} placement="top">
              <IconButton size="small" onClick={() => handleRetryByClick(row.retry_by)}>
                <ContactEmergency fontSize="small" color="secondary" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      ),
      sortable: false,
      width: "150px",
    });
  }

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "rgba(0,0,0,0.025)",
        borderBottomWidth: "2px",
        borderBottomColor: "rgba(0,0,0,0.08)",
        minHeight: "44px",
      },
    },
    headCells: {
      style: {
        paddingLeft: "12px",
        paddingRight: "8px",
        fontWeight: 700,
        fontSize: "0.72rem",
        color: "#64748b",
        letterSpacing: "0.04em",
      },
    },
    rows: {
      style: {
        minHeight: "52px",
        borderBottomColor: "rgba(0,0,0,0.05) !important",
        "&:hover": { backgroundColor: "rgba(59,130,246,0.04)" },
      },
    },
    cells: {
      style: { paddingLeft: "12px", paddingRight: "8px" },
    },
  };

  return (
    <Box>
      {/* Mini stats row */}
      {searchLoaded && (
        <Fade in timeout={400}>
          <Grid container spacing={1.5} mb={3}>
            <Grid item xs={6} sm={3}>
              <MiniStat
                label="Total"
                value={totalRows}
                color="#6366f1"
                icon={<JobIcon fontSize="small" />}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MiniStat
                label="Completed"
                value={totalCompletedJobs}
                color="#22c55e"
                icon={<CheckCircleOutline fontSize="small" />}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MiniStat
                label="Running"
                value={totalRunningJobs}
                color="#3b82f6"
                icon={<PlayCircleOutline fontSize="small" />}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MiniStat
                label="Failed"
                value={totalFailedJobs}
                color="#ef4444"
                icon={<ErrorOutline fontSize="small" />}
              />
            </Grid>
          </Grid>
        </Fade>
      )}

      {/* Toolbar */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        gap={2}
        flexWrap="wrap"
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <Avatar
            sx={{
              width: 32,
              height: 32,
              background: "linear-gradient(135deg, #6366f1, #4f46e5)",
              fontSize: "0.85rem",
            }}
          >
            <JobIcon sx={{ fontSize: "1rem" }} />
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight={700} lineHeight={1}>
              {toTitleCase(jobName)}
            </Typography>
            {searchLoaded && (
              <Typography variant="caption" color="text.secondary">
                {totalRows} record{totalRows !== 1 ? "s" : ""}
              </Typography>
            )}
          </Box>
        </Stack>
        <Stack direction="row" gap={1} alignItems="center">
          <Tooltip title={t("lang.settings") || "Settings"} arrow>
            <IconButton
              onClick={() => handleSettingsClick(table_id)}
              sx={{ border: "1.5px solid", borderColor: "divider", borderRadius: 2, p: 0.75 }}
            >
              <SettingsOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("lang.reset") || "Refresh"} arrow>
            <IconButton
              onClick={handleReset}
              sx={{ border: "1.5px solid", borderColor: "divider", borderRadius: 2, p: 0.75 }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <TextField
            size="small"
            placeholder={t("lang.actions.search") || "Search records…"}
            value={searchInput}
            onChange={handleSearchChange}
            sx={{
              width: 220,
              "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: "0.875rem" },
            }}
          />
        </Stack>
      </Stack>

      {/* Business ID filter */}
      {/* <Box
        sx={{
          mb: 2,
          p: 2,
          borderRadius: 2.5,
          border: "1.5px solid",
          borderColor: "divider",
          background:
            "linear-gradient(135deg, rgba(99, 102, 241, 0.06) 0%, rgba(79, 70, 229, 0.04) 100%)",
        }}
      >
        <Typography
          variant="caption"
          fontWeight={700}
          color="text.secondary"
          sx={{ display: "block", mb: 1, letterSpacing: 0.4 }}
        >
          Search by Business ID
        </Typography>
        <TextField
          size="small"
          fullWidth
          placeholder="Enter business ID to filter job payloads…"
          value={businessIdInput}
          onChange={handleBusinessIdChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <BusinessIcon fontSize="small" sx={{ color: "primary.main", opacity: 0.85 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            maxWidth: { xs: "100%", sm: 420 },
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              fontSize: "0.875rem",
              backgroundColor: "background.paper",
            },
          }}
        />
      </Box> */}

      {/* Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2.5,
          border: "1.5px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <DataTable
          className={table_id}
          key={`${orderColumn}-${orderDirection}-${perPage}-${data.length}`}
          columns={columns}
          data={data}
          progressPending={loading}
          progressComponent={
            <Box sx={{ width: "100%", py: 4 }}>
              <LinearProgress sx={{ mx: 3, borderRadius: 3, mb: 2 }} />
              {[...Array(5)].map((_, i) => (
                <Stack key={i} direction="row" gap={2} px={3} py={0.75}>
                  <Skeleton width={60} height={20} />
                  <Skeleton width="40%" height={20} />
                  <Skeleton width={120} height={20} />
                  <Skeleton width={80} height={20} />
                </Stack>
              ))}
            </Box>
          }
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          onChangeRowsPerPage={handlePerPageChange}
          onChangePage={handlePageChange}
          onSort={handleSort}
          sortServer
          paginationPerPage={perPage}
          paginationRowsPerPageOptions={items_per_page_options}
          paginationDefaultPage={page}
          clearSelectedRows
          responsive
          selectableRows={false}
          pointerOnHover
          fixedHeader
          persistTableHead
          expandableRows
          expandableRowsComponent={({ data: rowData }) => (
            <ExpandedRow
              data={rowData}
              dateFormat={date_format}
              dateTimeFormat={date_time_format}
            />
          )}
          sortIcon={() => <></>}
          customStyles={customStyles}
          highlightOnHover
          noDataComponent={
            <Box sx={{ py: 8, textAlign: "center" }}>
              <JobIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
              <Typography color="text.secondary">No records found.</Typography>
            </Box>
          }
          conditionalRowStyles={[
            {
              when: (row) => row.is_verification_job === true,
              style: {
                backgroundColor: "rgba(25, 118, 210, 0.08)",
                borderLeft: "3px solid #1976d2",
              },
            },
          ]}
        />
      </Paper>

      <AlertMessage {...errorState} />

      {/* Settings drawer */}
      <MBDrawer
        open={openSettingsDrawer}
        onClose={() => setOpenSettingsDrawer(false)}
        title={t("lang.settings") || "Settings"}
        anchor="right"
        height="100vh"
        width="400px"
      >
        <PageSettings
          saveBtnLoading={settingsSaveBtnLoading}
          currentSortColumn={orderColumn}
          currentSortDirection={orderDirection}
          currentItemsPerPage={perPage}
          tableId={tableId}
          defaultSortColumn={defaultSortColumnFromSettings}
          defaultSortDirection={defaultSortDirectionFromSettings}
          defaultItemsPerPage={defaultItemsPerPageFromSettings}
          sortableColumns={[
            { value: "id", label: t("lang.id") || "ID" },
            { value: "status", label: t("lang.status") || "Status" },
            { value: "created_at", label: t("lang.date") || "Date" },
          ]}
          onSettingsChange={handleSettingsChange}
        />
      </MBDrawer>

      {/* Cancel dialog */}
      <Dialog
        open={cancelPopupOpen}
        onClose={handleCancelPopupClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t("lang.reason_optional") || "Cancel Job"}
        </DialogTitle>
        <DialogContent>
          <TextField
            multiline
            rows={4}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            fullWidth
            variant="outlined"
            placeholder={t("lang.enter_reason_for_canceling_this_job") || "Enter reason (optional)"}
            sx={{
              mt: 1,
              "& .MuiOutlinedInput-root": { borderRadius: 2, padding: "10px !important" },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <SoftButton
            onClick={handleCancelPopupClose}
            variant="text"
            color="dark"
            disabled={cancellingJobId === cancelJobId}
          >
            {t("lang.close") || "Close"}
          </SoftButton>
          <SoftButton
            onClick={handleCancelJobConfirm}
            color="error"
            variant="contained"
            disabled={cancellingJobId === cancelJobId}
            startIcon={
              cancellingJobId === cancelJobId ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {t("lang.cancel_job") || "Cancel Job"}
          </SoftButton>
        </DialogActions>
      </Dialog>

      {/* Retry by dialog */}
      <Dialog
        open={retryByPopupOpen}
        onClose={handleRetryByPopupClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t("lang.retry_by_information") || "Retry History"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {retryByData?.length > 0 ? (
              retryByData.map((retry, idx) => (
                <Paper
                  key={idx}
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 1.5,
                    borderRadius: 2,
                    border: "1.5px solid",
                    borderColor: "#3b82f633",
                    bgcolor: "rgba(59,130,246,0.04)",
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Retry #{idx + 1}
                    </Typography>
                    {retry.timestamp && (
                      <Typography variant="caption" color="text.secondary">
                        {formatDateWithFormat(new Date(retry.timestamp * 1000), date_time_format)}
                      </Typography>
                    )}
                  </Stack>
                  <Stack direction="row" gap={3} flexWrap="wrap">
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t("lang.user_name") || "User"}:
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {retry.user_name || "N/A"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t("lang.user_id") || "User ID"}:
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {retry.user_id || "N/A"}
                      </Typography>
                    </Box>
                    {retry.retry_job_id && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t("lang.retry_job_id") || "Retry Job ID"}:
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {retry.retry_job_id}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                  {retry.retry_jobs && Object.keys(retry.retry_jobs).length > 0 && (
                    <Stack direction="row" gap={3} flexWrap="wrap" mt={1.5}>
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 0.75, display: "block" }}
                        >
                          {t("lang.retry_jobs") || "Retry Jobs"}:
                        </Typography>
                        <Stack spacing={0.5}>
                          {Object.entries(retry.retry_jobs).map(([sub_job_id, bwh_job_id]) => (
                            <Paper
                              key={sub_job_id}
                              variant="outlined"
                              sx={{
                                px: 1.5,
                                py: 0.75,
                                borderRadius: 1.5,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                flexWrap: "wrap",
                              }}
                            >
                              <Chip
                                label={`Sub Job #${sub_job_id}`}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ fontWeight: 700, fontSize: "0.7rem" }}
                              />
                              <Tooltip title={bwh_job_id} placement="top">
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontFamily: "monospace",
                                    bgcolor: "action.hover",
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: 1,
                                    cursor: "default",
                                    maxWidth: 220,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    display: "block",
                                  }}
                                >
                                  {bwh_job_id}
                                </Typography>
                              </Tooltip>
                            </Paper>
                          ))}
                        </Stack>
                      </Box>
                    </Stack>
                  )}
                </Paper>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
                {t("lang.no_retry_information_available") || "No retry information available."}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <SoftButton onClick={handleRetryByPopupClose} variant="contained" color="dark">
            {t("lang.close") || "Close"}
          </SoftButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

JobRecords.propTypes = {
  jobName: PropTypes.string.isRequired,
};

export default JobRecords;
